import React, { useState, useEffect } from 'react';
import Footer from './Footer';
import Header from './Header';
import { Share2, BookOpen } from "lucide-react"; // ✅ added icons

// Appwrite imports
import { Client, Databases, Storage, Query } from 'appwrite';

// Initialize Appwrite client
const client = new Client();
client
  .setEndpoint('https://cloud.appwrite.io/v1')
  .setProject('67fbee6a001260770104');

const databases = new Databases(client);
const storage = new Storage(client);

const DATABASE_ID = '68098bc80034d68e4e64';
const COLLECTION_ID = '68098bd900226cbeb1cd';
const BUCKET_ID = '680990d6000f00f9d400';

const KNOWLEDGE_CATEGORIES = [
  'Tech Tips',
  'Study Hacks',
  'Life Lessons',
  'Career Talk',
  'Faith & Morals',
  'Money Matters',
  'Personal Development'
];

const FeaturedKnowledge = () => {
  const [knowledgePosts, setKnowledgePosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [shareModal, setShareModal] = useState(null); // ✅ added share modal state

  const availableCategories = ['All', ...KNOWLEDGE_CATEGORIES];

  useEffect(() => {
    const fetchKnowledgePosts = async () => {
      setLoading(true);
      try {
        const response = await databases.listDocuments(
          DATABASE_ID,
          COLLECTION_ID,
          [
            Query.equal('isApproved', true),
            Query.equal('contentType', 'Knowledge Hub'),
            Query.orderDesc('$createdAt')
          ]
        );

        setKnowledgePosts(response.documents);
      } catch (error) {
        console.error('Error fetching knowledge posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledgePosts();
  }, []);

  const getImageUrl = (imageId) => {
    if (!imageId) return null;
    try {
      return storage.getFileView(BUCKET_ID, imageId);
    } catch (error) {
      console.error('Error generating image URL:', error);
      return '';
    }
  };

  // ✅ Generate knowledge post link
  const generateShareLink = (postId) => {
    return `${window.location.origin}/knowledge/${postId}`;
  };

  // ✅ Copy link to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        alert("Link copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  // ✅ Sharing functions
  const shareViaWhatsApp = (post) => {
    const link = generateShareLink(post.$id);
    const message = `Check out this knowledge article "${post.title}" on PoemXtra: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const shareViaTwitter = (post) => {
    const link = generateShareLink(post.$id);
    const message = `"${post.title}" - Knowledge Hub on PoemXtra`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        message
      )}&url=${encodeURIComponent(link)}`,
      "_blank"
    );
  };

  const shareViaFacebook = (post) => {
    const link = generateShareLink(post.$id);
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
      "_blank"
    );
  };

  const filteredKnowledge = knowledgePosts.filter((post) => {
    const matchesCategory =
      selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <>
      <Header />
      <section id="knowledge" className="py-16 px-6" style={{ backgroundColor: '#f0f4f8' }}>
        <h3 className="text-3xl font-semibold text-center mb-6">Featured Knowledge Hub</h3>

        <div className="flex flex-wrap justify-center mb-6 gap-3">
          {availableCategories.map((category) => (
            <button
              key={category}
              className={`px-4 py-2 rounded-full transition text-sm font-medium ${
                selectedCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-300 text-gray-800'
              } hover:bg-blue-500 hover:text-white`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="flex justify-center mb-8">
          <input
            type="text"
            placeholder="Search knowledge posts by title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {loading ? (
          <div className="text-center py-10">
            <p>Loading knowledge posts...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredKnowledge.length > 0 ? (
              filteredKnowledge.map((post) => (
                <div key={post.$id} className="bg-white shadow-lg rounded-lg hover:shadow-xl transition-shadow overflow-hidden">
                  <div className="w-full aspect-[3/2] bg-gray-100 flex items-center justify-center">
                    {post.imageId ? (
                      <img
                        src={getImageUrl(post.imageId)}
                        alt={post.title}
                        className="w-full h-full object-contain object-center"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x200?text=Image+Not+Found';
                        }}
                      />
                    ) : (
                      <div className="h-full w-full bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-400 italic">No image</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h2 className="text-xl font-bold mb-2">{post.title}</h2>
                    {post.category && (
                      <div className="mb-2">
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                          {post.category}
                        </span>
                      </div>
                    )}
                    <p className="text-gray-700 mb-4 whitespace-pre-line line-clamp-4">
                      {post.content}
                    </p>

                    {/* ✅ Action Buttons */}
                    <div className="flex justify-between items-center mt-3">
                      <button
                        onClick={() =>
                          window.open(`/knowledge/${post.$id}`, "_blank")
                        }
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                      >
                        <BookOpen size={16} />
                        Read More
                      </button>
                      <button
                        onClick={() => setShareModal(post)}
                        className="flex items-center gap-1 text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
                      >
                        <Share2 size={16} />
                        Share
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center col-span-full">No approved knowledge hub posts found in this category.</p>
            )}
          </div>
        )}
      </section>

      {/* ✅ Share Modal */}
      {shareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">
              Share "{shareModal.title}"
            </h3>

            {/* Share Link */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Share Link:
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={generateShareLink(shareModal.$id)}
                  readOnly
                  className="flex-1 p-2 border rounded text-sm"
                />
                <button
                  onClick={() =>
                    copyToClipboard(generateShareLink(shareModal.$id))
                  }
                  className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600 transition-colors text-sm"
                >
                  Copy
                </button>
              </div>
            </div>

            {/* Social Share Buttons */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Share on:
              </label>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => shareViaWhatsApp(shareModal)}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors text-sm"
                >
                  WhatsApp
                </button>
                <button
                  onClick={() => shareViaTwitter(shareModal)}
                  className="bg-blue-400 text-white px-4 py-2 rounded hover:bg-blue-500 transition-colors text-sm"
                >
                  Twitter
                </button>
                <button
                  onClick={() => shareViaFacebook(shareModal)}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors text-sm"
                >
                  Facebook
                </button>
              </div>
            </div>

            {/* Close Button */}
            <div className="flex justify-end">
              <button
                onClick={() => setShareModal(null)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
};

export default FeaturedKnowledge;